/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Container,
    Form,
    Row,
    Col,
    Spinner,
    Button,
    Alert,
} from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";

import { exportListCriteria, resetExport } from "../lists/listSlice";
import FormTextInput from "../../common/components/FormTextInput";
import FormLabel from "../../common/components/form/FormLabel";
import CheckBox from "../../common/components/CheckBox";
import Modal from '../../common/components/Modal';

import './SaveModal.scss';

// Fallback when the organization's subscription doesn't specify an export
// limit. The real limit comes from subscription.features.engage.listExportRecordLimit.
const DEFAULT_EXPORT_RECORD_LIMIT = 50000;

const ExportModal = (props) => {
    const {
        handleClose,
        onExportSuccess,
        title,
    } = props;

    const dispatch = useDispatch();
    const listDataState = useSelector(state => state.list);

    const universeState = useSelector(state => state.universe);
    const universeQueryState = useSelector(state => state.universeQuery);

    const currentUniverse = universeState.currentUniverse;
    const exploreByContact = universeState.universes[currentUniverse]?.exploreByContact ?? false;
    const modeKey = exploreByContact ? 'CONTACT' : 'COMPANY';

    const filterEntities = universeState.universes[currentUniverse]?.entities || {};
    const currentFilters = Object.values(filterEntities);

    const exportRecordLimit = Number(universeState.universes[currentUniverse]?.subscription?.features?.engage?.listExportRecordLimit)
        || DEFAULT_EXPORT_RECORD_LIMIT;

    const currentResults = universeQueryState.results[currentUniverse]?.[modeKey];
    const countResult = exploreByContact
        ? (currentResults?.totalContacts || 0)
        : (currentResults?.totalCompanies || 0);
    const cubeQuery = currentResults?.appliedFilters?.cubeQuery || [];
    const cubeName = currentResults?.appliedFilters?.cubeName;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [notifyByEmailOnSuccessOrFailure, setNotifyByEmailOnSuccessOrFailure] = useState('');
    const [notifyByEmailCheckbox, setNotifyByEmailCheckbox] = useState(false);

    useEffect(() => {
        if (listDataState?.status?.status === 'succeeded') {
            onExportSuccess();
            handleClose();
        }
    }, [listDataState?.status?.status, onExportSuccess, handleClose]);

    useEffect(() => {
        return () => {
            dispatch(resetExport());
        };
    }, [dispatch]);

    const isValid = () => {
        if (!name.trim()) return false;
        if (countResult > exportRecordLimit) return false;
        if (notifyByEmailCheckbox && !notifyByEmailOnSuccessOrFailure.trim()) return false;
        return true;
    };

    const isBusy = () => listDataState?.loading || false;
    const isError = () => listDataState?.status?.status === 'error';

    const doExport = () => {
        const type = exploreByContact ? 'EXPORT_CONTACT' : 'EXPORT_COMPANY';

        const emailNotifications = notifyByEmailCheckbox && notifyByEmailOnSuccessOrFailure
            ? notifyByEmailOnSuccessOrFailure.split(';').map(e => e.trim()).filter(Boolean)
            : [];

        dispatch(exportListCriteria({
            name: name.trim(),
            type,
            description: description.trim(),
            filters: currentFilters,
            cubeQuery: cubeQuery,
            cubeName: cubeName,
            notifyByEmailOnSuccessOrFailure: emailNotifications,
        }));
    };

    if (listDataState?.status?.status === 'succeeded') {
        return null;
    }

    return (
        <Modal
            showClose
            size={'lg'}
            title={title}
            handleClose={handleClose}
            showHeaderLine
            footer={
                <div className="footer">
                    <Button
                        variant='info'
                        disabled={isBusy() || !isValid() || countResult === 0}
                        onClick={doExport}
                    >
                        {!isBusy() ? 'Create' : (
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        )}
                    </Button>
                    <Button
                        disabled={isBusy()}
                        variant='outline-secondary'
                        onClick={handleClose}
                        style={{ marginLeft: '10px' }}
                    >
                        Cancel
                    </Button>
                </div>
            }
        >
            <Container className="container">
                <Form noValidate>
                    <Form.Group id="exportList">
                        <Row>
                            <Col xs={12}>
                                {countResult === 0 && (
                                    <Alert variant={'warning'} transition={true}>
                                        No records to export. Please adjust your filters.
                                    </Alert>
                                )}
                                {countResult > exportRecordLimit && (
                                    <Alert variant={'danger'} transition={true}>
                                        There is a {exportRecordLimit.toLocaleString()} record limit on Export. Please add additional filters to reduce the count or contact your Account Representative.
                                    </Alert>
                                )}
                                {isError() && (
                                    <Alert variant={'danger'} transition={true}>
                                        {listDataState.status.error
                                            ? `Unable to export list, there was an error [${listDataState.status.error}]. Please contact your administrator.`
                                            : 'Unable to export list, there was an error. Please contact your administrator.'}
                                    </Alert>
                                )}
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col xs={2}><Form.Label id={'form-control-name-label-id'}>Name</Form.Label></Col>
                            <Col xs={8}>
                                <Form.Control
                                    id={'form-control-name-input-id'}
                                    size="md"
                                    type="text"
                                    placeholder="Please enter name."
                                    isInvalid={!name.trim()}
                                    onChange={(e) => setName(e.target.value)}
                                    value={name}
                                />
                                {!name.trim() && (
                                    <Form.Control.Feedback type="invalid">
                                        Name is required.
                                    </Form.Control.Feedback>
                                )}
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col xs={2}><FormLabel>Description</FormLabel></Col>
                            <Col xs={8}>
                                <Form.Control
                                    id={'form-control-desc-text-area-id'}
                                    as={'textarea'}
                                    rows={5}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={2}><FormLabel>Options</FormLabel></Col>
                            <Col xs={8}>
                                <CheckBox
                                    label={'Notify me when my export completes'}
                                    checked={notifyByEmailCheckbox}
                                    onChange={(e) => {
                                        setNotifyByEmailCheckbox(e);
                                        if(!e) setNotifyByEmailOnSuccessOrFailure('');
                                    }}
                                />
                                <FormTextInput
                                    placeholder={'Multiple emails can be provided, separate with semi-colon'}
                                    className={'input'}
                                    size={'sm'}
                                    value={notifyByEmailOnSuccessOrFailure}
                                    onChange={setNotifyByEmailOnSuccessOrFailure}
                                    disabled={!notifyByEmailCheckbox}
                                />
                            </Col>
                        </Row>
                    </Form.Group>
                </Form>
            </Container>
        </Modal>
    );
};

ExportModal.propTypes = {
    title: PropTypes.string,
    handleClose: PropTypes.func,
    onExportSuccess: PropTypes.func,
};

ExportModal.defaultProps = {
    handleClose: () => {},
    onExportSuccess: () => {},
    title: 'Export List'
};

export default ExportModal;